import clsx from "clsx";
import { ReactNode, useEffect, useState } from "react";

import { makeId } from "../utils";

// import "@aurora/style/root.scss";
import styles from "./aurora-root.module.scss";

type AuroraRootProps = {
  /** Optional extra class(es) */
  className?: string;
  /** Light or Dark theme */
  darkMode?: boolean;
  /** Removes all default top and left margins on components. */
  noMarginMode?: boolean;
  /** Everything that uses Aurora components */
  children: ReactNode;
};

export const AURORA_ROOT_ELEMENT = makeId();

/**
 * AuroraRoot needs to be the root element of the application in order to
 * make sure the focus indicators for keyboard navigation works properly.
 *
 * Example:
 * const container = document.getElementById("root");
 * const root = createRoot(container as HTMLElement);
 * root.render(
 *   <AuroraRoot>
 *     <App />
 *   </AuroraRoot>
 * );
 */
function AuroraRoot({
  className,
  children,
  darkMode = false,
  noMarginMode = false,
}: AuroraRootProps) {
  // Store the root element ID in state to support SSR rehydration
  const [rootId, setRootId] = useState("");

  useEffect(() => {
    setRootId(AURORA_ROOT_ELEMENT);
  }, []);

  function handleKeyDown(e: KeyboardEvent) {
    if (e.metaKey || e.altKey || e.ctrlKey) return;
    document.body.dataset.interactionMode = "keyboard";
  }

  function handleMouseDown() {
    document.body.dataset.interactionMode = "pointer";
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleMouseDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, []);

  return (
    <div
      className={clsx(
        styles.root,
        className,
        darkMode && styles.dark,
        noMarginMode && styles.noDefaultMargins
      )}
      id={rootId}
    >
      {children}
    </div>
  );
}

export default AuroraRoot;
